import { Injectable } from '@angular/core';
import {
  CatalogApplicationsResponse,
  CatalogLoadSizesResponse,
  CatalogPumpCapacitiesResponse,
  CatalogRequest,
  CatalogSlumpsResponse,
  CatalogSpacingsResponse,
  CatalogsService,
  CatalogUnloadTimesResponse,
  CatalogUnloadTypesResponse,
  COUNTRY_CODE,
  ImSlump,
  Order,
  ImUnloadType,
  PlantsService,
  TechnicalProductOP,
} from '@cemex-core/cmx-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, catchError, mergeMap, tap, debounceTime, withLatestFrom } from 'rxjs/operators';
import { getOrderBuilderState, OrderBuilderFeatureState, OrderBuilderState } from '../../../reducers';
import {
  AddApplicationCatalogAction,
  AddUnloadTypeFilteredCompleteAction,
  CatalogCompleteAction,
  ChangeUnloadTimeCompleteAction,
  LoadApplicationCatalogAction,
  LoadApplicationCatalogCompleteAction,
  LoadAvailablePumpCapacitiesSuccessAction,
  LoadLoadSizeCatalogAction,
  LoadLoadSizeCatalogCompleteAction,
  LoadPumpCapacityAction,
  LoadSlumpCatalogAction,
  LoadSlumpCatalogCompleteAction,
  LoadSlumpFilteredCatalogAction,
  LoadSlumpProductCatalogAction,
  LoadSlumpProductCatalogCompleteAction,
  LoadSpacingCatalogAction,
  LoadSpacingCatalogCompleteAction,
  LoadTemperatureCatalogAction,
  // LoadTemperatureCatalogAction,
  LoadTemperatureCatalogCompleteAction,
  LoadUnloadTimeCatalogAction,
  LoadUnloadTimeCatalogCompleteAction,
  LoadUnloadTimeFilteredCompleteAction,
  LoadUnloadTypeCatalogAction,
  LoadUnloadTypeCatalogCompleteAction,
  LoadUnloadTypePlantCatalogAction,
  SingleCatalogsActionTypes,
  SingleCatalogsErrorAction,
} from '../actions/single-catalogs.actions';
import { getLocalSpacings, getUnloadTimes } from '../selectors/single-catalogs';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';

import { getOrderSelectedDetailState as RepeatDetail } from '@ordersnproducts/modules/new-repeat-order/selectors/order-selected-detail.selector';
import {
  getOrderSelectedDetail,
  getOrderSelectedDetailState as ModifyDetail,
} from '@ordersnproducts/modules/new-modify-order/selectors/order-selected-detail.selector';
import { TemperatureFeatures } from '../../../../shared/services/experiences.service';
import { Router } from '@angular/router';
import { UNLOAD_TYPE_MIN_TIMES_MAP } from '../../specification/constants/unload-type-min-times-map';
import { ImprovementsModifyProvider } from '@ordersnproducts/modules/new-modify-order/modules/rms-modify-product-specification/providers/improvements-modify.provider';
import isAfterCutOffTimeUK = ImprovementsModifyProvider.isAfterCutOffTimeUK;

@Injectable()
export class SingleCatalogsEffects {
  /**
   * Effect to handle the loading of application catalogs.
   *
   * This effect listens for the `LOAD_APPLICATION` action and performs the following steps:
   * 1. Checks if the current route URL contains 'modify'.
   * 2. Selects the current state of the order builder.
   * 3. Filters out any undefined or null states.
   * 4. Determines the country code from the state and sets a fetch limit if the country code is Mexico.
   * 5. If the route is in modify mode, it fetches the selected product type ID and makes a catalog request from the state.
   * 6. If not in modify mode, it makes a catalog request from the state without a selected product type ID.
   * 7. Calls the catalog service to get applications based on the request.
   * 8. Dispatches a `LoadApplicationCatalogCompleteAction` with the result applications on success.
   * 9. Dispatches a `SingleCatalogsErrorAction` with the error on failure.
   */
  getElementCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_APPLICATION),
      switchMap((action: LoadApplicationCatalogAction) => {
        const isModify = this.router.url.includes('modify');

        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          filter((state) => !!state),
          switchMap((state: OrderBuilderFeatureState) => {
            const countryCode = _safe(() => state.businessLinePage.shippingCondition.selected.countryCode.trim());
            let fetch = 0;
            if (countryCode === COUNTRY_CODE.MEXICO) {
              fetch = 500;
            }
            if (isModify) {
              return this.getSelectedProductTypeId().pipe(
                map((selectedProductTypeId) => this.makeCatalogRequestFromState(state, selectedProductTypeId)),
              );
            } else {
              return of(this.makeCatalogRequestFromState(state, undefined, fetch));
            }
          }),
          switchMap((request: CatalogRequest) =>
            this.catalogService.getApplications(request).pipe(
              map((result: CatalogApplicationsResponse) => new LoadApplicationCatalogCompleteAction(result.applications)),
              catchError((error) => of(new SingleCatalogsErrorAction(error))),
            ),
          ),
        );
      }),
    ),
  );

  getSpacingCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_SPACING),
      switchMap((action: LoadSpacingCatalogAction) => {
        const isModify = this.router.url.includes('modify');

        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          filter((state) => !!state),
          switchMap((state: OrderBuilderFeatureState) => {
            if (isModify) {
              return this.getSelectedProductTypeId().pipe(
                map((selectedProductTypeId) => this.makeCatalogRequestFromState(state, selectedProductTypeId)),
              );
            } else {
              return of(this.makeCatalogRequestFromState(state));
            }
          }),
          switchMap((request: CatalogRequest) =>
            this.catalogService.getSpacings(request).pipe(
              map((result: CatalogSpacingsResponse) => new LoadSpacingCatalogCompleteAction(result.spacings)),
              catchError((error) => of(new SingleCatalogsErrorAction(error))),
            ),
          ),
        );
      }),
    ),
  );

  getSpacingFilterCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.CHANGE_UNLOAD_TIME),
      switchMap(({ payload }) => {
        return this.store.pipe(select(getLocalSpacings)).pipe(
          take(1),
          map((spacings) => {
            if (payload != null) {
              const filterSpacing = spacings.filter((spacing) => spacing['spacingMinutes'] >= payload);
              return new ChangeUnloadTimeCompleteAction(filterSpacing);
            } else {
              return new ChangeUnloadTimeCompleteAction(spacings);
            }
          }),
        );
      }),
    ),
  );

  getUnloadTimeCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_UNLOAD_TIME),
      switchMap((action: LoadUnloadTimeCatalogAction) => {
        const isModify = this.router.url.includes('modify');

        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          filter((state) => !!state),
          switchMap((state: OrderBuilderFeatureState) => {
            if (isModify) {
              return this.getSelectedProductTypeId().pipe(
                map((selectedProductTypeId) => this.makeCatalogRequestFromState(state, selectedProductTypeId)),
              );
            } else {
              return of(this.makeCatalogRequestFromState(state));
            }
          }),
          withLatestFrom(this.store.select(getOrderSelectedDetail)),
          switchMap(([request, order]: [CatalogRequest, Order]) =>
            this.catalogService.getUnloadTimes(request).pipe(
              map((result: CatalogUnloadTimesResponse) => {
                const unloadType = _safe(() => order.orderProducts[0].itemProperties.unloadType.unloadTypeCode);
                if (unloadType) {
                  const maxUnloadTimeInMinutes = UNLOAD_TYPE_MIN_TIMES_MAP[unloadType];
                  const isAfterCutOff = isAfterCutOffTimeUK(_safe(() => order.programmedDateTime));

                  if (isAfterCutOff) {
                    return new LoadUnloadTimeCatalogCompleteAction({
                      unloadTimes: result.unloadTimes,
                      filteredUnloadTimes: result.unloadTimes.filter(
                        (unloadTime) => unloadTime.unloadTimeMinutes <= maxUnloadTimeInMinutes,
                      ),
                    });
                  }

                  return new LoadUnloadTimeCatalogCompleteAction({
                    unloadTimes: result.unloadTimes,
                    filteredUnloadTimes: result.unloadTimes.filter(
                      (unloadTime) => unloadTime.unloadTimeMinutes >= maxUnloadTimeInMinutes,
                    ),
                  });
                }
                return new LoadUnloadTimeCatalogCompleteAction({
                  unloadTimes: result.unloadTimes,
                  filteredUnloadTimes: result.unloadTimes,
                });
              }),
              catchError((error) => of(new SingleCatalogsErrorAction(error))),
            ),
          ),
        );
      }),
    ),
  );

  getUnloadTimeFilteredCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_UNLOAD_TIME_FILTERED),
      switchMap(({ payload }) => {
        return this.store.pipe(select(getUnloadTimes)).pipe(
          map((unloadTimes) => {
            if (payload != null) {
              const filteredUnloadTimes = [];
              for (let i = 0; i < unloadTimes.length; i++) {
                if (filteredUnloadTimes.length > 0) {
                  filteredUnloadTimes.push(unloadTimes[i]);
                } else {
                  unloadTimes[i].unloadTimeCode === payload && filteredUnloadTimes.push(unloadTimes[i]);
                }
              }
              return new LoadUnloadTimeFilteredCompleteAction(filteredUnloadTimes);
            } else {
              return new LoadUnloadTimeFilteredCompleteAction(unloadTimes);
            }
          }),
        );
      }),
    ),
  );

  getUnloadTypeCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_UNLOAD_TYPE),
      switchMap((action: LoadUnloadTypeCatalogAction) => {
        const isModify = this.router.url.includes('modify');

        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          filter((state) => !!state),
          switchMap((state: OrderBuilderFeatureState) => {
            if (isModify) {
              return this.getSelectedProductTypeId().pipe(
                map((selectedProductTypeId) => this.makeCatalogRequestFromState(state, selectedProductTypeId)),
              );
            } else {
              return of(this.makeCatalogRequestFromState(state));
            }
          }),
          switchMap((request: CatalogRequest) =>
            this.catalogService.getUnloadTypes(request).pipe(
              map((result: CatalogUnloadTypesResponse) => new LoadUnloadTypeCatalogCompleteAction(result.unloadTypes)),
              catchError((error) => of(new SingleCatalogsErrorAction(error))),
            ),
          ),
        );
      }),
    ),
  );

  getUnloadTypePlantCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_UNLOAD_TYPE_PLANT),
      switchMap((action: LoadUnloadTypePlantCatalogAction) => {
        return this.store.select(getOrderBuilderState).pipe(
          filter((state) => {
            if (_safe(() => state.locationPage.pointOfDelivery.pointOfDeliverySalesArea).length > 0) {
              return true;
            } else if (_safe(() => state.locationPage.jobsite.jobsiteSalesArea).length > 0) {
              return true;
            } else {
              return false;
            }
          }),
          take(1),
          map((state: OrderBuilderFeatureState) => this.makeCatalogRequestForPlant(state)),
          switchMap((request: CatalogRequest) =>
            this.catalogService.getUnloadTypes(request).pipe(
              map((result: CatalogUnloadTypesResponse) => new LoadUnloadTypeCatalogCompleteAction(result.unloadTypes)),
              catchError((error) => {
                return of(new SingleCatalogsErrorAction(error));
              }),
            ),
          ),
        );
      }),
    ),
  );

  getLoadSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_LOAD_SIZE),
      switchMap((action: LoadLoadSizeCatalogAction) => {
        const isModify = this.router.url.includes('modify');

        return this.store.select(getOrderBuilderState).pipe(
          filter((state) => !!state),
          take(1),
          switchMap((state: OrderBuilderFeatureState) => {
            if (isModify) {
              return this.getSelectedProductTypeId().pipe(
                map((selectedProductTypeId) => this.makeCatalogRequestFromState(state, selectedProductTypeId)),
              );
            } else {
              return of(this.makeCatalogRequestFromState(state));
            }
          }),
          switchMap((request: CatalogRequest) =>
            this.catalogService.getLoadSizes(request).pipe(
              map((result: CatalogLoadSizesResponse) => new LoadLoadSizeCatalogCompleteAction(result.loadSizes)),
              catchError((error) => of(new SingleCatalogsErrorAction(error))),
            ),
          ),
        );
      }),
    ),
  );

  getSlump$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_SLUMP),
      switchMap((action: LoadSlumpCatalogAction) => {
        const isModify = this.router.url.includes('modify');

        return this.store.select(getOrderBuilderState).pipe(
          filter((state) => !!state),
          take(1),
          switchMap((state: OrderBuilderFeatureState) => {
            if (isModify) {
              return this.getSelectedProductTypeId().pipe(
                map((selectedProductTypeId) => this.makeCatalogRequestFromState(state, selectedProductTypeId)),
              );
            } else {
              return of(this.makeCatalogRequestFromState(state));
            }
          }),
          switchMap((request: CatalogRequest) =>
            this.catalogService.getSlumps(request).pipe(
              map((result: CatalogSlumpsResponse) => new LoadSlumpCatalogCompleteAction(result.slumps)),
              catchError((error) => of(new SingleCatalogsErrorAction(error))),
            ),
          ),
        );
      }),
    ),
  );

  /**
   * Same behaviour as getSlump$ but filtering according to the available
   * slumps at the technicalProds from the selected commercialProd
   * TO-DO: REFACTOR TO AVOID SENDING A REQUEST ON EACH EFFECT
   */

  getFilteredSlump$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_SLUMP_FILTERED),
      switchMap((action: LoadSlumpFilteredCatalogAction) => {
        let pumpabilitySelected;
        return this.store.select(getOrderBuilderState).pipe(
          debounceTime(500), // LET THE VALUES TO CATCH UP WITH ONCHANGE EVENT FOR PUMPABILITY
          filter((state) => !!state),
          tap(
            (state) =>
              (pumpabilitySelected = _safe(
                () => state.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.pumpability,
              )),
          ),
          take(1),
          map((state: OrderBuilderFeatureState) => {
            if (
              state.locationPage.experiences.activeExperienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK &&
              state.businessLinePage.shippingCondition.selected.countryCode.trim() === COUNTRY_CODE.FRANCE
            ) {
              return this.makeCatalogRequestForSlump(state);
            } else {
              return this.makeCatalogRequestFromState(state);
            }
          }),
          switchMap((request: CatalogRequest) =>
            this.catalogService.getSlumps(request).pipe(
              map((result: CatalogSlumpsResponse) => {
                const availableAtTechProds = action.payload.map((techProd: TechnicalProductOP) => {
                  if (pumpabilitySelected === undefined || pumpabilitySelected === Number(techProd['pumpable'])) {
                    return techProd['slump'].slumpCode;
                  }
                });
                let filtered = result.slumps.map((slump) => {
                  if (availableAtTechProds.includes(slump.slumpCode)) {
                    return slump;
                  }
                  return null;
                });
                filtered = filtered.filter((el) => el !== null);
                return new LoadSlumpCatalogCompleteAction(filtered);
              }),
              catchError((error) => {
                return of(new SingleCatalogsErrorAction(error));
              }),
            ),
          ),
        );
      }),
    ),
  );

  getProductSlump$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_SLUMP_PRODUCT),
      mergeMap((action: LoadSlumpProductCatalogAction) => {
        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          filter((state) => !!state && !!state.businessLinePage.businessLine),
          switchMap((state: OrderBuilderFeatureState) => {
            const request = this.makeSlumpRequest(state, action.payload ? action.payload.productCode : null);
            if (!request.plantCode) {
              const plantId = _safe(() => state.locationPage.jobsite.jobsiteSalesArea[0].plant.plantId);
              if (plantId) {
                return this.plantsService.getPlant(plantId).pipe(
                  map((plant) => {
                    return { ...request, plantCode: _safe(() => plant.plantCode) } as CatalogRequest;
                  }),
                );
              } else {
                return of(request);
              }
            }
            return of(request);
          }),
          switchMap((request: CatalogRequest) => {
            return this.catalogService.getSlumps(request).pipe(
              switchMap((result) => {
                // VERIFIES AVAILABILITY FOR SLUMP OBTAINED FROM COMMAND ORDER
                return combineLatest([this.store.select(RepeatDetail), this.store.select(ModifyDetail)]).pipe(
                  take(1),
                  map(([repeatData, modifyData]) => {
                    const orderDetail = !!repeatData.order ? repeatData.order : modifyData.order;
                    // WORKING FOR ORDERS-V AND SUMMARIES DETAILS
                    if (_safe(() => !!orderDetail.items)) {
                      orderDetail.items.forEach((item) => {
                        if (item.product.productCode === request.productCode) {
                          const prodSlump = _safe(() => item.orderItemProfile.slumpType) as any;
                          const foundAvailable = result.slumps.find((slump) => slump.slumpCode === prodSlump.slumpCode);
                          if (!!prodSlump && !foundAvailable) {
                            result.slumps = result.slumps.concat(prodSlump as ImSlump);
                          }
                        }
                      });
                    } else if (_safe(() => !!orderDetail.orderProducts)) {
                      orderDetail.orderProducts.forEach((orderProduct) => {
                        if (orderProduct?.productCode?.includes(request.productCode)
                          || orderProduct?.['technicalProduct']?.productCode?.includes(request.productCode)) {
                          const prodSlump = _safe(() => orderProduct.itemProperties.slump) as any;
                          const foundAvailable = result.slumps.find((slump) => slump.slumpCode === prodSlump.slumpCode);
                          if (!!prodSlump && !foundAvailable) {
                            result.slumps = result.slumps.concat(prodSlump as ImSlump);
                          }
                        }
                      });
                    }
                    if (request.productCode) {
                      return new LoadSlumpProductCatalogCompleteAction({
                        [request.productCode.replace(/\b0+/g, '')]: result,
                      });
                    } else {
                      return new LoadSlumpProductCatalogCompleteAction({});
                    }
                  }),
                );
              }),
              catchError((error) => {
                console.log(error);
                return of(new SingleCatalogsErrorAction(error));
              }),
            );
          }),
        );
      }),
    ),
  );

  getPumpCapacities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_PUMP_CAPACITY),
      switchMap((action: LoadPumpCapacityAction) => {
        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          filter((state) => !!state),
          map((state: OrderBuilderFeatureState) => {
            return this.makeCatalogRequestFromState(state);
          }),
          switchMap((response: CatalogRequest) => {
            return this.catalogService.getPumpCapacities(response).pipe(
              map((result: CatalogPumpCapacitiesResponse) => {
                return new LoadAvailablePumpCapacitiesSuccessAction(result);
              }),
              catchError((error) => {
                return of(error);
              }),
            );
          }),
        );
      }),
    ),
  );

  validateLoadTypeUK$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_UNLOAD_TYPE_COMPLETED),
      switchMap((action: LoadUnloadTypeCatalogCompleteAction) => {
        const combineOrderAndState$ = combineLatest([
          this.store.select(getOrderBuilderState),
          this.store.select(RepeatDetail),
          this.store.select(ModifyDetail),
        ]);
        return combineOrderAndState$.pipe(
          take(1),
          filter((state) => !!state),
          map(([state, repeatOrderDetail, modifyOrderDetail]) => {
            const orderDetail = !!repeatOrderDetail.order ? repeatOrderDetail.order : modifyOrderDetail.order;
            const countryCode = _safe(() => state.businessLinePage.shippingCondition.selected.countryCode.trim(), null);
            const isUK = countryCode === COUNTRY_CODE.UNITED_KINGDOM.trim();
            const unloadType = _safe(() => orderDetail.orderProducts[0].itemProperties.unloadType, null);
            if (isUK && unloadType != null) {
              const unloadTypes = state.rmsSpecificationPage.singleCatalogsState.unloadTypes;

              const unloadTypeExist = unloadTypes.find((types) => types.unloadTypeId === unloadType.unloadTypeId);
              if (!unloadTypeExist) {
                const newsUnloadTypes = [];
                const newUnloadType = unloadType as ImUnloadType;
                newsUnloadTypes.push(newUnloadType);
                return new AddUnloadTypeFilteredCompleteAction(newsUnloadTypes);
              } else {
                return new CatalogCompleteAction();
              }
            }
            return new CatalogCompleteAction();
          }),
        );
      }),
    ),
  );

  validateApplicationUK$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_APPLICATION_COMPLETED),
      switchMap((action: LoadUnloadTypeCatalogCompleteAction) => {
        const combineOrderAndState$ = combineLatest([
          this.store.select(getOrderBuilderState),
          this.store.select(RepeatDetail),
          this.store.select(ModifyDetail),
        ]);
        return combineOrderAndState$.pipe(
          take(1),
          filter((state) => !!state),
          map(([state, repeatOrderDetail, modifyOrderDetail]) => {
            const orderDetail = !!repeatOrderDetail.order ? repeatOrderDetail.order : modifyOrderDetail.order;
            const countryCode = _safe(() => state.businessLinePage.shippingCondition.selected.countryCode.trim(), null);
            const isUK = countryCode === COUNTRY_CODE.UNITED_KINGDOM.trim();
            const application = _safe(() => orderDetail.orderProducts[0].itemProperties.application, null);
            if (isUK && application != null) {
              const applications = state.rmsSpecificationPage.singleCatalogsState.applications;

              const applicationExist = applications.find((app) => app.applicationId === application.applicationId);
              if (!applicationExist) {
                return new AddApplicationCatalogAction(application);
              } else {
                return new CatalogCompleteAction();
              }
            }
            return new CatalogCompleteAction();
          }),
        );
      }),
    ),
  );

  getTemperaturesCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SingleCatalogsActionTypes.LOAD_TEMPERATURE),
      switchMap((action: LoadTemperatureCatalogAction) => {
        const feature = action.payload.feature === TemperatureFeatures.ICE ? 'ICE_QUANTITY' : 'TEMPERATURE';
        const catalogRequest: CatalogRequest = {
          jobsiteCode: action.payload.jobsite.shipmentLocationCode,
          orderFeatureGroup: feature,
        };
        return this.catalogService.getFeatures(catalogRequest);
      }),
      map((response) => {
        if (!!response.features && response.features.length > 0) {
          return new LoadTemperatureCatalogCompleteAction(response.features);
        } else {
          return new LoadTemperatureCatalogCompleteAction([]);
        }
      }),
    ),
  );

  /**
   * Creates a CatalogRequest object based on the provided state and optional parameters.
   *
   * @param state - The current state of the OrderBuilderFeature.
   * @param selectedProductTypeId - An optional product type ID to override the one in the state.
   * @param fetch - An optional fetch value to include in the CatalogRequest.
   * @returns A CatalogRequest object containing the product type ID and optionally the fetch value.
   */
  private makeCatalogRequestFromState(
    state: OrderBuilderFeatureState,
    selectedProductTypeId?: number | null,
    fetch?: number,
  ): CatalogRequest {
    const businessLine = state.businessLinePage.businessLine.selected;
    const isModify = this.router.url.includes('modify');
    let productTypeId;
    if (isModify) {
      productTypeId = selectedProductTypeId || _safe(() => businessLine.productTypeId);
    } else {
      productTypeId = _safe(() => businessLine.productTypeId);
    }
    if (!!fetch) {
      return {
        productTypeId,
        fetch,
      } as CatalogRequest;
    }
    return {
      productTypeId,
    } as CatalogRequest;
  }

  /**
   *  Get the selected product type id
   *  @return {Observable<number>}
   * */
  private getSelectedProductTypeId() {
    return this.store.select(getOrderSelectedDetail).pipe(
      map((state) => {
        const productTypeId = state.orderProducts[0].productType.productTypeId;
        return productTypeId ? productTypeId : null;
      }),
      take(1),
    );
  }

  /**
   * @param state
   * @returns
   */
  private makeCatalogRequestForPlant(state: OrderBuilderFeatureState): CatalogRequest {
    return {
      productTypeId: state.businessLinePage.businessLine.selected.productTypeId,
      plantId:
        _safe(() => state.locationPage.pointOfDelivery.pointOfDeliverySalesArea[0].plant.plantId) ||
        _safe(() => state.locationPage.jobsite.jobsiteSalesArea[0].plant.plantId),
    } as CatalogRequest;
  }

  /**
   *
   * @param {OrderBuilderFeatureState} state
   * @return {SlumpRequest}
   */
  private makeSlumpRequest(state: OrderBuilderFeatureState, productCode?: string): CatalogRequest {
    const params = {
      productTypeId: _safe(() => state.businessLinePage.businessLine.selected.productTypeId, undefined),
      plantCode:
        _safe(() => state.locationPage.calendars.calendars[0].bigOrder.plantCode) ||
        _safe(() => state.locationPage.jobsite.plant[0].plantCode),
      ...(productCode && { productCode }),
    } as CatalogRequest;
    if (params.productTypeId === undefined) {
      delete params.productTypeId;
    }
    return params;
  }

  /**
   * @param state
   * @return {CatalogRequest}
   */
  private makeCatalogRequestForSlump(state: OrderBuilderFeatureState): CatalogRequest {
    return {
      productTypeId: state.businessLinePage.businessLine.selected.productTypeId,
      jobsiteCode: state.locationPage.jobsite.selected.shipmentLocationCode,
    } as CatalogRequest;
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private catalogService: CatalogsService,
    private plantsService: PlantsService,
    private router: Router,
  ) {}
}
